/**
 * Company Settings Page (Settings)
 * The tenant's company details, default payment terms, late-fee settings and
 * how flat contract recurring schedules are billed (tracker item 34).
 *
 * Every signed-in role can view them; only ADMIN can save (backend
 * requireRole('ADMIN') on PUT /company-settings), so other roles get a
 * read-only form. The late-fee rate is entered as a percentage (1.5%) but
 * stored as a fraction (0.0150). Also auto-invoicing (tracker item 37): when
 * completed jobs are invoiced automatically.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAPIClient, ICompanySettingsApi } from '../../api';
import { getErrorMessage } from '../../utils/apiError';
import { canManageCompanySettings } from '../../utils/permissions';
import {
  COMPANY_TEXT_FIELDS,
  CompanySettingsForm,
  CompanySettingsFormErrors,
  buildCompanySettingsRequest,
  toCompanySettingsForm,
} from '../../utils/companySettings';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { convertSpeedText, workDaysToList, workDaysToText } from '../../utils/companySettings';
import { GeocodeStatusChip } from '../../components/GeocodeStatusChip';
import * as types from '../../api/types';
import { WorkDaysPicker } from './TechnicianSchedulesPage';
import { Toast } from '../../components/Toast';

/** IANA names the browser knows, for the timezone suggestions (the server validates). */
const TIMEZONE_SUGGESTIONS: string[] = (() => {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  } catch {
    return [];
  }
})();

type FieldName = keyof CompanySettingsForm;

interface FieldProps {
  name: FieldName;
  label: string;
  form: CompanySettingsForm;
  errors: CompanySettingsFormErrors;
  disabled: boolean;
  onChange: (name: FieldName, value: string) => void;
  required?: boolean;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  placeholder?: string;
  suffix?: string;
  hint?: string;
  className?: string;
  /** id of a <datalist> of suggestions */
  list?: string;
}

function Field({ name, label, form, errors, disabled, onChange, required, type = 'text', inputMode, placeholder, suffix, hint, className, list }: FieldProps) {
  const id = `company-${name}`;
  const error = errors[name];
  const maxLength = (COMPANY_TEXT_FIELDS as Record<string, number>)[name];
  return (
    <div className={className}>
      <label htmlFor={id} className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
      </label>
      <div className="relative">
        <input
          id={id}
          type={type}
          inputMode={inputMode}
          value={form[name]}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={placeholder}
          list={list}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={`${inputClass}${suffix ? ' pr-14' : ''}`}
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">{suffix}</span>
        )}
      </div>
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 text-xs text-red-700">{error}</p>
      ) : (
        hint && <p className="mt-1.5 text-xs text-gray-500">{hint}</p>
      )}
    </div>
  );
}

export function CompanySettingsPage() {
  const { user } = useAuth();
  const apiClient = React.useMemo<ICompanySettingsApi>(() => createAPIClient(), []);
  const canManage = canManageCompanySettings(user);

  const [saved, setSaved] = useState<CompanySettingsForm | null>(null);
  /** The company address's geocode (read-only, tracker item 41) */
  const [location, setLocation] = useState<{ status: types.GeocodeStatus; precision: types.GeocodePrecision | null } | null>(null);
  const [form, setForm] = useState<CompanySettingsForm | null>(null);
  const [fieldErrors, setFieldErrors] = useState<CompanySettingsFormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const settings = await apiClient.getCompanySettings();
        if (!active) return;
        const loaded = toCompanySettingsForm(settings);
        setLocation(settings.geocodeStatus ? { status: settings.geocodeStatus, precision: settings.geocodePrecision ?? null } : null);
        setSaved(loaded);
        setForm(loaded);
      } catch (err) {
        if (active) setError(getErrorMessage(err, 'Failed to load company settings'));
      } finally {
        if (active) setIsLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [apiClient]);

  const setField = (name: FieldName, value: string) => {
    setForm((prev) => {
      if (!prev) return prev;
      // Switching miles / kilometers converts the speed already typed
      if (name === 'distanceUnit') return { ...prev, distanceUnit: value, routingSpeed: convertSpeedText(prev.routingSpeed, prev.distanceUnit, value) };
      return { ...prev, [name]: value };
    });
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setNotice(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !canManage) return;

    const result = buildCompanySettingsRequest(form);
    if ('errors' in result) {
      setFieldErrors(result.errors);
      setError('Fix the highlighted fields and save again.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setFieldErrors({});
      const response = await apiClient.updateCompanySettings(result.request);
      const updated = toCompanySettingsForm(response);
      setLocation(response.geocodeStatus ? { status: response.geocodeStatus, precision: response.geocodePrecision ?? null } : null);
      setSaved(updated);
      setForm(updated);
      setNotice('Company settings saved.');
    } catch (err) {
      // Backend 400s name the field: "Invalid company settings: field: message; ..."
      setError(getErrorMessage(err, 'Failed to save company settings'));
    } finally {
      setIsSaving(false);
    }
  };

  const disabled = !canManage || isSaving;
  const isDirty = !!form && !!saved && JSON.stringify(form) !== JSON.stringify(saved);
  const fieldProps = form ? { form, errors: fieldErrors, disabled, onChange: setField } : null;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="mb-6">
        <Link to="/settings" className="inline-block py-2 sm:py-0 text-sm font-semibold text-gray-500 hover:text-blue-600">
          ← Settings
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1 break-words">Company</h1>
        {!canManage && !isLoading && form && (
          <p className="mt-1 text-sm text-gray-600">Only administrators can change company settings.</p>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {notice && <Toast kind="success">{notice}</Toast>}

      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {!isLoading && fieldProps && (
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <section className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Company details</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
              <Field {...fieldProps} name="companyName" label="Company name" required className="sm:col-span-4" />
              <Field {...fieldProps} name="taxId" label="Tax ID" className="sm:col-span-2" />
              <Field {...fieldProps} name="addressLine1" label="Address line 1" className="sm:col-span-6" />
              <Field {...fieldProps} name="addressLine2" label="Address line 2" className="sm:col-span-6" />
              <Field {...fieldProps} name="city" label="City" className="sm:col-span-3" />
              <Field {...fieldProps} name="state" label="State" className="sm:col-span-1" />
              <Field {...fieldProps} name="postalCode" label="Postal code" className="sm:col-span-2" />
              <Field {...fieldProps} name="phone" label="Phone" type="tel" className="sm:col-span-2" />
              <Field {...fieldProps} name="email" label="Email" type="email" className="sm:col-span-4" />
              <Field {...fieldProps} name="website" label="Website" type="url" placeholder="https://example.com" className="sm:col-span-3" />
              <Field
                {...fieldProps}
                name="logoUrl"
                label="Logo URL"
                type="url"
                placeholder="https://example.com/logo.png"
                hint="A link to an image; uploading is not supported yet."
                className="sm:col-span-3"
              />
              <div className="sm:col-span-6">
                <label htmlFor="company-invoiceFooterNote" className={labelClass}>Invoice footer note</label>
                <textarea
                  id="company-invoiceFooterNote"
                  rows={3}
                  value={fieldProps.form.invoiceFooterNote}
                  onChange={(e) => setField('invoiceFooterNote', e.target.value)}
                  disabled={disabled}
                  maxLength={COMPANY_TEXT_FIELDS.invoiceFooterNote}
                  placeholder="Thank you for your business."
                  className={inputClass}
                />
                {fieldErrors.invoiceFooterNote && (
                  <p className="mt-1.5 text-xs text-red-700">{fieldErrors.invoiceFooterNote}</p>
                )}
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Billing defaults</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
              <Field
                {...fieldProps}
                name="defaultPaymentTermsDays"
                label="Payment terms"
                inputMode="numeric"
                suffix="days"
                hint="New invoices are due this many days after the invoice date unless you pick a due date."
                className="sm:col-span-3"
              />
            </div>
          </section>

          <section className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Late fees</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
              <Field
                {...fieldProps}
                name="financeChargePercent"
                label="Finance charge rate"
                inputMode="decimal"
                suffix="%"
                hint="Per period, e.g. 1.5"
                className="sm:col-span-2"
              />
              <Field
                {...fieldProps}
                name="financeChargeGraceDays"
                label="Grace period"
                inputMode="numeric"
                suffix="days"
                className="sm:col-span-2"
              />
              <Field
                {...fieldProps}
                name="financeChargeMinAmount"
                label="Minimum charge ($)"
                inputMode="decimal"
                className="sm:col-span-2"
              />
            </div>
          </section>

          <section className="bg-white rounded-lg shadow p-4 sm:p-6" aria-labelledby="company-flat-contract-heading">
            <h2 id="company-flat-contract-heading" className="text-base font-semibold text-gray-900 mb-1">Flat contract billing</h2>
            <p className="mb-4 max-w-3xl text-sm text-gray-600">
              Recurring schedules billed as a flat contract get one invoice per billing period for the contract amount, at the start of
              each period. Choose who issues those invoices.
            </p>
            <div role="radiogroup" aria-label="Flat contract billing" className="space-y-3 text-sm">
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="company-flatContractBillingMode"
                  className="mt-1 shrink-0"
                  value="MANUAL"
                  checked={fieldProps.form.flatContractBillingMode === 'MANUAL'}
                  disabled={disabled}
                  onChange={() => setField('flatContractBillingMode', 'MANUAL')}
                />
                <span>
                  <span className="font-semibold">Manual</span> (recommended to start): nothing is billed until the office reviews the due
                  periods and bills them on Invoices &gt; Bill flat contracts.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="company-flatContractBillingMode"
                  className="mt-1 shrink-0"
                  value="AUTOMATIC"
                  checked={fieldProps.form.flatContractBillingMode === 'AUTOMATIC'}
                  disabled={disabled}
                  onChange={() => setField('flatContractBillingMode', 'AUTOMATIC')}
                />
                <span>
                  <span className="font-semibold">Automatic</span>: the server's background recurring generator bills each contract's
                  current period when it runs. It runs only when the server has it turned on (RECURRING_JOBS_ENABLED); missed earlier
                  periods are never billed automatically and stay on the Bill flat contracts page. Automatic invoices are posted under
                  the oldest active administrator.
                </span>
              </label>
            </div>
            {fieldErrors.flatContractBillingMode && (
              <p className="mt-1.5 text-xs text-red-700">{fieldErrors.flatContractBillingMode}</p>
            )}
          </section>

          <section className="bg-white rounded-lg shadow p-4 sm:p-6" aria-labelledby="company-inbox-heading">
            <h2 id="company-inbox-heading" className="text-base font-semibold text-gray-900 mb-1">Customer conversations inbox</h2>
            <p className="mb-4 max-w-3xl text-sm text-gray-600">
              The Inbox holds customer emails and customer portal messages. Choose who on your team can read and answer them.
              Customers never see the inbox; they only see their own messages in the customer portal.
            </p>
            <div role="radiogroup" aria-label="Who can use the inbox" className="space-y-3 text-sm">
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="company-inboxAccess"
                  className="mt-1 shrink-0"
                  value="STAFF"
                  checked={fieldProps.form.inboxAccess === 'STAFF'}
                  disabled={disabled}
                  onChange={() => setField('inboxAccess', 'STAFF')}
                />
                <span>
                  <span className="font-semibold">Office staff</span> (recommended): administrators and office managers.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="company-inboxAccess"
                  className="mt-1 shrink-0"
                  value="EVERYONE"
                  checked={fieldProps.form.inboxAccess === 'EVERYONE'}
                  disabled={disabled}
                  onChange={() => setField('inboxAccess', 'EVERYONE')}
                />
                <span>
                  <span className="font-semibold">Everyone</span>: technicians too. They see every conversation, can reply to customers
                  and add internal notes.
                </span>
              </label>
            </div>
            {fieldErrors.inboxAccess && <p className="mt-1.5 text-xs text-red-700">{fieldErrors.inboxAccess}</p>}
          </section>

          <section className="bg-white rounded-lg shadow p-4 sm:p-6" aria-labelledby="company-auto-invoicing-heading">
            <h2 id="company-auto-invoicing-heading" className="text-base font-semibold text-gray-900 mb-1">Auto-invoicing</h2>
            <p className="mb-4 max-w-3xl text-sm text-gray-600">
              Create invoices for completed jobs without opening New invoice. Invoices are created as drafts (already in the ledger, like
              any new invoice) for the property's billing customer, dated the day they are created, with no discounts applied. Each
              customer's billing method decides the grouping: <strong>per visit</strong> customers get one invoice per job;{' '}
              <strong>consolidated</strong> customers get one invoice per month for all of that month's jobs, created after the month
              ends. Jobs covered by a flat contract, held jobs and excluded customers are never auto-invoiced. You can always run it by
              hand on Invoices &gt; Auto-invoicing.
            </p>
            <div role="radiogroup" aria-label="Auto-invoicing" className="space-y-3 text-sm">
              {[
                { value: 'OFF', title: 'Off', text: 'nothing is invoiced automatically. The Auto-invoicing page still lets the office run it by hand.' },
                {
                  value: 'ON_COMPLETION',
                  title: 'When a job is completed',
                  text: 'a per-visit customer\'s job is invoiced right after it is marked completed. Consolidated customers wait for the nightly run after month end, which also picks up anything missed.',
                },
                {
                  value: 'NIGHTLY',
                  title: 'Nightly',
                  text: 'once a day, from the hour below, every eligible completed job is invoiced.',
                },
              ].map((option) => (
                <label key={option.value} className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="company-autoInvoicingMode"
                    className="mt-1 shrink-0"
                    value={option.value}
                    checked={fieldProps.form.autoInvoicingMode === option.value}
                    disabled={disabled}
                    onChange={() => setField('autoInvoicingMode', option.value)}
                  />
                  <span>
                    <span className="font-semibold">{option.title}</span>: {option.text}
                  </span>
                </label>
              ))}
            </div>
            {fieldErrors.autoInvoicingMode && <p className="mt-1.5 text-xs text-red-700">{fieldErrors.autoInvoicingMode}</p>}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-6">
              <Field
                {...fieldProps}
                name="autoInvoicingStartDate"
                label="Start date"
                type="date"
                hint="Only jobs completed on or after this date are auto-invoiced. Left blank, it becomes the day you turn auto-invoicing on, so older jobs are never swept up."
                className="sm:col-span-3"
              />
              <Field
                {...fieldProps}
                name="autoInvoicingNightlyHour"
                label="Nightly run hour (UTC)"
                inputMode="numeric"
                hint="0-23. The nightly run happens at the first check after this hour (UTC)."
                className="sm:col-span-3"
              />
            </div>
            <label className="mt-4 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                checked={fieldProps.form.autoInvoicingAutoSend === 'true'}
                disabled={disabled}
                onChange={(e) => setField('autoInvoicingAutoSend', e.target.checked ? 'true' : 'false')}
              />
              <span>
                <span className="font-semibold">Send the invoices automatically</span>: send each new invoice right away instead of leaving it
                as a draft to review, and email it when notifications are on (customers who opted out of notifications are not emailed).
              </span>
            </label>
            <p className="mt-3 text-xs text-gray-500">
              The automatic runs post under the oldest active administrator, and the nightly run happens only when the server has it turned
              on (AUTO_INVOICING_ENABLED).
            </p>
          </section>

          <section className="bg-white rounded-lg shadow p-4 sm:p-6" aria-labelledby="company-scheduling-heading">
            <h2 id="company-scheduling-heading" className="text-base font-semibold text-gray-900 mb-1">Scheduling</h2>
            <p className="mb-4 text-sm text-gray-600">
              Job start times and the dispatch board's "today" are in this timezone. Working days and hours are the default for every
              technician (override them under Technician schedules); the board only warns about work outside them.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Field name="timezone" label="Timezone" {...fieldProps} placeholder="America/New_York" list="company-timezone-options" hint="An IANA name such as America/Chicago." />
                <datalist id="company-timezone-options">
                  {TIMEZONE_SUGGESTIONS.map((tz) => <option key={tz} value={tz} />)}
                </datalist>
              </div>
              <Field name="defaultJobDurationMinutes" label="Default job duration" {...fieldProps} type="number" suffix="min"
                hint="Used when a job's work items have no typical durations." />
              <div className="sm:col-span-2">
                <span className={labelClass}>Working days</span>
                <WorkDaysPicker idPrefix="company-workday" value={workDaysToList(fieldProps.form.workDays)} disabled={disabled}
                  onChange={(days) => setField('workDays', workDaysToText(days))} />
                {fieldErrors.workDays && <p className="mt-1.5 text-xs text-red-700">{fieldErrors.workDays}</p>}
              </div>
              <Field name="workdayStart" label="Working hours start" {...fieldProps} type="time" />
              <Field name="workdayEnd" label="Working hours end" {...fieldProps} type="time" />
            </div>

            <h3 id="company-routing-heading" className="mt-6 text-sm font-semibold text-gray-900">Travel between jobs</h3>
            <p className="mt-1 mb-4 max-w-3xl text-sm text-gray-600">
              Used to estimate drive time from straight-line distance between geocoded addresses. Every technician's day starts from the
              company address above.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="company-distanceUnit" className={labelClass}>Distance unit</label>
                <select
                  id="company-distanceUnit"
                  value={fieldProps.form.distanceUnit}
                  disabled={disabled}
                  onChange={(e) => setField('distanceUnit', e.target.value)}
                  className={inputClass}
                >
                  <option value="MI">Miles (mph)</option>
                  <option value="KM">Kilometers (km/h)</option>
                </select>
                {fieldErrors.distanceUnit && <p className="mt-1.5 text-xs text-red-700">{fieldErrors.distanceUnit}</p>}
              </div>
              <Field name="routingSpeed" label="Average driving speed" {...fieldProps} inputMode="decimal"
                suffix={fieldProps.form.distanceUnit === 'KM' ? 'km/h' : 'mph'}
                hint="Typical speed between jobs, stops and traffic included." />
              <Field name="routingCircuityFactor" label="Road distance factor" {...fieldProps} inputMode="decimal"
                hint="How much longer roads are than a straight line: 1.30 means 30% longer. Use more where rivers or highways force detours." />
              <Field name="routingUnknownTravelMinutes" label="Travel time when a location is unknown" {...fieldProps} inputMode="numeric" suffix="min"
                hint="Assumed when an address could not be placed on the map." />
              <div className="sm:col-span-2 text-sm">
                <span className={labelClass}>Company location</span>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {location ? (
                    <GeocodeStatusChip status={location.status} precision={location.precision} />
                  ) : (
                    <span className="text-gray-500">Unknown</span>
                  )}
                  <span className="text-gray-500">
                    {location?.status === 'NOT_FOUND'
                      ? 'Check the company address: it could not be found on the map.'
                      : location?.status === 'PENDING'
                        ? 'The address is looked up in the background after it is saved.'
                        : ''}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {canManage && (
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setForm(saved);
                  setFieldErrors({});
                  setError(null);
                }}
                disabled={isSaving || !isDirty}
                className={`${secondaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`}
              >
                Discard changes
              </button>
              <button type="submit" disabled={isSaving || !isDirty} className={`${primaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`}>
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </form>
      )}
    </main>
  );
}
